import { useCallback, useEffect, useMemo } from "react";
import { useDebounce, useIsSanctioned } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { policy } from "@sablier/v2-machines";
import { BigNumber, _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import {
  useAccount,
  useMachineEmptyProxy,
  useModalTokenPicker,
  useModalTransaction,
  useProxyBankAccessor,
  useProxyBankField,
  useProxyBankForm,
  useResetter,
  useTokenBalance,
} from "~/client/hooks";
import type { IToken } from "@sablier/v2-types";

export function useWatcher() {
  const token = useProxyBankField("token");
  const update = useProxyBankForm((state) => state.api.updateFields);
  const reset = useProxyBankForm((state) => state.api.resetFields);
  const { chainId, proxy } = useAccount();
  const { humanized: balance, isLoading } = useTokenBalance({
    owner: proxy,
    token: token.field.value,
  });

  useEffect(() => {
    update({
      amount: {
        isLoading,
        value: !balance.isZero() ? balance.toString() : "0",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, update, isLoading]);

  useEffect(() => {
    reset(["amount", "token"]);
  }, [chainId, reset]);
}

export function useFieldToken() {
  const { t } = useT();
  const token = useProxyBankField("token");
  const { setOpen } = useModalTokenPicker();
  const { isSanctioned } = useIsSanctioned(token.field.value?.address);

  const onChoose = useCallback(
    (value?: IToken) => {
      vendors.track.log((events) => {
        return events.selectToken(value?.name ?? "");
      });
      token.update({
        value: {
          ...value!,
          image: value?.image ?? undefined,
        },
        warning: undefined,
      });
    },
    [token],
  );

  const onChange = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "tokenPicker",
        placeKey: "createSingle",
      });
    });
    setOpen(true, {
      chosen: token.field.value,
      onChoose,
    });
  }, [onChoose, setOpen, token]);

  /** Update the token warning if the asset is resolved as sanctioned by TRM */
  useEffect(() => {
    if (isSanctioned) {
      token.update({ warning: policy.token.sanctioned(t) });
    }
  }, [isSanctioned, t, token]);

  return {
    token,
    onChange,
    onChoose,
  };
}

export function useFieldAmount() {
  const amount = useProxyBankField("amount");
  const token = useProxyBankField("token");

  return {
    amount,
    token,
  };
}

export function useFormPrepare() {
  const { t } = useT();
  const amount = useProxyBankField("amount");
  const fields = useProxyBankForm((state) => state.fields);

  const { machine, send } = useMachineEmptyProxy();
  const error = useDebounce(machine.context.error, 300);

  const isPrepared = useMemo(
    () =>
      !new BigNumber(amount.field.value || "0").isZero() &&
      _.isNilOrEmptyString(error),
    [amount.field.value, error],
  );

  useEffect(() => {
    send("CHECK", { payload: { api: { t }, fields } });
  }, [fields, t, send]);

  return {
    isPrepared,
  };
}

export function useFormSubmit() {
  const access = useProxyBankAccessor();
  const { machine, send } = useMachineEmptyProxy();
  const { setOpen, updateData, doClose } = useModalTransaction();
  const { signer, isConfigured, proxy } = useAccount();
  const { proxy: reset } = useResetter();
  const error = useMemo(() => machine.context.error, [machine.context.error]);
  const { t } = useT();

  const doSubmit = useCallback(() => {
    const state = access();
    if (isConfigured) {
      send("SAVE", {
        payload: {
          api: {
            reset,
            setOpen,
            t,
            updateData,
            doClose,
          },
          fields: state.fields,
          signer,
          proxy,
        },
      });
    }
  }, [
    access,
    isConfigured,
    reset,
    send,
    setOpen,
    doClose,
    signer,
    t,
    updateData,
    proxy,
  ]);

  return {
    doSubmit,
    error,
  };
}
